import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './context/AuthContext';
import { setupGlobalErrorHandling } from './utils/globalErrorHandler';
import { initTikTokPixel } from './utils/tiktokPixel';
import { debugTikTokPixel } from './utils/debugTikTokPixel';
import posthog from 'posthog-js';

// Set up global error handling
setupGlobalErrorHandling();

// Initialize TikTok Pixel only if enabled AND user has granted consent
function hasMarketingConsent() {
  try {
    const enabled = process.env.REACT_APP_ENABLE_TIKTOK_PIXEL === 'true';
    if (!enabled) {
      console.log('[TikTok Pixel] Not enabled. Set REACT_APP_ENABLE_TIKTOK_PIXEL=true');
      return false;
    }
    const consent = localStorage.getItem('marketing_consent') === 'true';
    if (!consent) {
      console.log('[TikTok Pixel] Waiting for user consent. Accept cookies to enable.');
    }
    return consent;
  } catch (_) {
    return false;
  }
}

const shouldLoadPixel = hasMarketingConsent();
console.log('[TikTok Pixel] Loading:', shouldLoadPixel);
initTikTokPixel(shouldLoadPixel);

// Make debug function available globally
if (shouldLoadPixel) {
  window.debugTikTokPixel = debugTikTokPixel;
  console.log('[TikTok Pixel] Debug helper available. Run: debugTikTokPixel()');
}

// Initialize PostHog (additive: preserves existing Vercel analytics)
try {
  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
  const posthogHost = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.posthog.com';
  if (posthogKey) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      capture_pageview: false, // we send page views on route changes via RouterAnalytics
      autocapture: false, // explicit taxonomy only
      session_recording: { enabled: hasMarketingConsent(), maskAllInputs: true, sampleRate: 0.1 }
    });
    // Register feature flags callback (best-effort)
    try { posthog.onFeatureFlags(() => {}); } catch (_) {}
    // Expose for debugging in console
    try { window.posthog = posthog; } catch (_) {}
  }
} catch (_) {}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Only use StrictMode in development to avoid double-rendering in production
// This improves INP (Interaction to Next Paint) performance
const AppWrapper = process.env.NODE_ENV === 'development' ? (
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
) : (
  <BrowserRouter>
    <AuthProvider>
      <App />
    </AuthProvider>
  </BrowserRouter>
);

root.render(AppWrapper);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
